/**
 * Canonical operational metric definitions.
 *
 * Every metric on the operations dashboard and the reports engine MUST derive
 * from these functions (or the equivalent SQL expression) so a number shown in
 * two places is never computed two different ways. Each function documents:
 *   - Formula
 *   - Source table(s) / event(s)
 *   - Known limitation
 *
 * These are pure and deterministic — no date math depends on "now" unless a
 * `now` value is passed in — so they are unit-testable against fixed fixtures.
 */

export const MS_PER_HOUR = 3_600_000;
export const MS_PER_DAY = 86_400_000;

/** A timestamped boundary shared by the cycle-time family. */
export interface Boundary {
  startedAt: string | Date;
  completedAt: string | Date | null;
}

/** Convert a value to epoch ms, returning null when absent/invalid. */
function toMs(value: string | Date | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const t = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
}

/** Duration in days between two timestamps (null if either is missing). */
export function durationDays(a: string | Date | null, b: string | Date | null): number | null {
  const aMs = toMs(a);
  const bMs = toMs(b);
  if (aMs === null || bMs === null) return null;
  return (bMs - aMs) / MS_PER_DAY;
}

/** Duration in hours between two timestamps (null if either is missing). */
export function durationHours(a: string | Date | null, b: string | Date | null): number | null {
  const days = durationDays(a, b);
  return days === null ? null : days * 24;
}

/** Arithmetic mean of finite numbers; null when the list is empty. */
export function mean(values: number[]): number | null {
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length === 0) return null;
  return finite.reduce((s, v) => s + v, 0) / finite.length;
}

/** Median of finite numbers; null when the list is empty. */
export function median(values: number[]): number | null {
  const finite = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (finite.length === 0) return null;
  const mid = Math.floor(finite.length / 2);
  return finite.length % 2 === 0 ? (finite[mid - 1] + finite[mid]) / 2 : finite[mid];
}

/**
 * Order cycle time (days).
 * Formula:  fully_fulfilled_at − order_created_at
 * Source:   sales_orders.created_at → fulfilments.shipped_at (the point at which
 *           the order is fully out the door). Falls back to fulfilments.created_at
 *           when no shipped_at is recorded.
 * Limitation: requires both boundaries; orders never fulfilled are excluded, not zero.
 */
export function orderCycleTimeDays(createdAt: string | Date, fulfilledAt: string | Date | null): number | null {
  return durationDays(createdAt, fulfilledAt);
}

/** Average order cycle time across orders (Part K fixture: 5×1d + 5×3d → 2d). */
export function averageOrderCycleTimeDays(orders: Boundary[]): number | null {
  const durations = orders
    .map((o) => orderCycleTimeDays(o.startedAt, o.completedAt))
    .filter((d): d is number => d !== null);
  return mean(durations);
}

/**
 * Allocation time (hours).
 * Formula:  first_allocation_at − order_created_at
 * Source:   sales_orders.created_at → sales_order_events (event_type = 'allocated')
 *           or sales_order_items first non-zero qty_allocated timestamp.
 * Limitation: relies on a recorded allocation event; absent events → null.
 */
export function allocationTimeHours(createdAt: string | Date, allocatedAt: string | Date | null): number | null {
  return durationHours(createdAt, allocatedAt);
}

/**
 * Pick time (minutes).
 * Formula:  pick_completed_at − pick_started_at
 * Source:   pick_tasks.started_at → pick_tasks.completed_at
 * Limitation: only pick tasks that reached completion are counted.
 */
export function pickTimeMinutes(startedAt: string | Date | null, completedAt: string | Date | null): number | null {
  const h = durationHours(startedAt, completedAt);
  return h === null ? null : h * 60;
}

/**
 * Pack time (minutes).
 * Formula:  packing_completed_at − packing_started_at
 * Source:   packing_verifications.last_scan_at (completion) − first scan at created_at
 * Limitation: packing is scan-driven; if no scans recorded, pack time is unavailable.
 */
export function packTimeMinutes(startedAt: string | Date | null, completedAt: string | Date | null): number | null {
  const h = durationHours(startedAt, completedAt);
  return h === null ? null : h * 60;
}

/**
 * Fulfilment time (days).
 * Formula:  shipped_at − order_created_at
 * Source:   sales_orders.created_at → fulfilments.shipped_at
 * Limitation: same boundaries as cycle time; treats shipment as fulfilment completion.
 */
export function fulfilmentTimeDays(createdAt: string | Date, shippedAt: string | Date | null): number | null {
  return durationDays(createdAt, shippedAt);
}

/**
 * Supplier lead time (days).
 * Formula:  received_at − ordered_at
 * Source:   purchase_orders.order_date → purchase_receipts.received_at
 * Limitation: only POs that have at least one receipt contribute an actual lead time.
 */
export function supplierLeadTimeDays(orderedAt: string | Date, receivedAt: string | Date | null): number | null {
  return durationDays(orderedAt, receivedAt);
}

export interface PurchaseOrderDelivery {
  expectedDate: string | Date | null;
  receivedAt: string | Date | null;
  /** whether the PO is still open (not fully received). */
  open: boolean;
}

/**
 * Late delivery rate (0..1).
 * Formula:  late_POs / total_POs (where "late" = received after expected date, or
 *           still open with expected date already past).
 * Source:   purchase_orders.expected_date + purchase_receipts.received_at
 * Limitation: requires an expected_date to judge lateness; POs without one are excluded.
 */
export function lateDeliveryRate(pos: PurchaseOrderDelivery[], now: string | Date = new Date()): number | null {
  const nowMs = toMs(now)!;
  const judged = pos.filter((p) => p.expectedDate !== null);
  if (judged.length === 0) return null;
  const late = judged.filter((p) => {
    const exp = toMs(p.expectedDate);
    if (exp === null) return false;
    if (p.receivedAt !== null) {
      const rec = toMs(p.receivedAt);
      return rec !== null && rec > exp;
    }
    // Still open and past its expected date → late.
    return p.open && exp < nowMs;
  });
  return late.length / judged.length;
}

/**
 * Fill rate (0..1).
 * Formula:  received_units / ordered_units
 * Source:   purchase_order_items.qty_received / qty_ordered
 * Limitation: divide-by-zero guarded; 0/0 → null (no evidence).
 */
export function fillRate(receivedUnits: number, orderedUnits: number): number | null {
  if (orderedUnits <= 0) return null;
  return Math.max(0, Math.min(1, receivedUnits / orderedUnits));
}

/**
 * Receipt variance (−1..∞).
 * Formula:  (received − ordered) / ordered
 * Source:   purchase_receipt_items.qty_received vs purchase_order_items.qty_ordered
 * Limitation: positive = over-receipt, negative = short receipt, 0 = exact.
 */
export function receiptVariance(receivedUnits: number, orderedUnits: number): number | null {
  if (orderedUnits <= 0) return null;
  return (receivedUnits - orderedUnits) / orderedUnits;
}

/**
 * Return rate (0..∞).
 * Formula:  returned_units / fulfilled_units (over a defined period)
 * Source:   sales_return_items.qty_returned vs fulfilment ledger entries (sale_fulfilment)
 * Limitation: period boundaries must be applied upstream; 0 fulfilled → null.
 */
export function returnRate(returnedUnits: number, fulfilledUnits: number): number | null {
  if (fulfilledUnits <= 0) return null;
  return returnedUnits / fulfilledUnits;
}

/**
 * Inventory turnover (turns/period).
 * Formula:  cost_of_goods_sold / average_inventory_value (same period)
 * Source:   sale_fulfilment ledger (units × unit_cost) / average inventory_balances valuation
 * Limitation: requires trustworthy unit_cost; if costing is missing, do not report.
 */
export function inventoryTurnover(cogs: number, avgInventoryValue: number): number | null {
  if (avgInventoryValue <= 0) return null;
  return cogs / avgInventoryValue;
}

/**
 * Days of stock (days).
 * Formula:  available / average_daily_demand
 * Source:   inventory_balances.available / (sale_fulfilment units ÷ period_days)
 * Limitation: zero demand → null (not "infinite"); negative available → 0.
 */
export function daysOfStock(available: number, averageDailyDemand: number): number | null {
  if (averageDailyDemand <= 0) return null;
  return Math.max(0, available / averageDailyDemand);
}

/**
 * Gross margin (currency).
 * Formula:  revenue − COGS
 * Source:   revenue from sales_orders (fulfilled) / sales_order_items total;
 *           COGS from inventory_ledger.unit_cost × sale_fulfilment units.
 * Limitation: only meaningful when COGS is trustworthy and complete; if any
 *           fulfilment line lacks a unit_cost, treat margin as "insufficient data"
 *           rather than silently under-reporting.
 */
export function grossMargin(revenue: number, cogs: number): number | null {
  if (revenue <= 0) return null;
  return revenue - cogs;
}

/**
 * Gross margin rate (0..1).
 * Formula:  (revenue − COGS) / revenue
 * Limitation: null when revenue <= 0 (insufficient data, not "zero margin").
 */
export function grossMarginRate(revenue: number, cogs: number): number | null {
  if (revenue <= 0) return null;
  return (revenue - cogs) / revenue;
}